import { Modal, TrackedButton, useSnackbar } from '@automata/ui';
import { logger, useQueryParam } from '@automata/utils';
import { useKillPlan } from '@automata/wfb-api';
import { Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box/Box';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

export type Props = {
  planId: string | undefined;
};

const CancelPlanningModalButton = ({ planId }: Props) => {
  const queryClient = useQueryClient();

  const [modalOpen, setModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const workflowId = useQueryParam('id');

  const { mutate: killPlan, isLoading: killPlanIsLoading } = useKillPlan({
    onError: (killPlanError) => {
      logger.error(killPlanError);
      enqueueSnackbar('Failed to cancel planning', { variant: 'error' });
    },
    onSuccess: () => {
      enqueueSnackbar('Planning cancelled successfully', {
        variant: 'success',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['v3', 'workflow', workflowId, 'plan', planId],
      });
      setModalOpen(false);
    },
  });

  const handleClickCancelPlanning = () => {
    if (!planId) {
      logger.warn(
        'No planId provided to CancelPlanningButton, cannot cancel planning.'
      );
      return;
    }

    killPlan({
      pathParams: {
        planId,
        workflowId,
      },
    });
  };

  return (
    <>
      <TrackedButton
        trackLabel="workflowPlanning.cancelPlanning"
        onClick={() => setModalOpen(true)}
        variant="contained"
        color="error"
        loading={killPlanIsLoading}
        disabled={!planId || killPlanIsLoading}
      >
        Cancel planning
      </TrackedButton>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Cancel planning"
      >
        <Box>
          <Typography pb={5} variant="body1">
            This will cancel current planning operation and can&apos;t be
            undone.
          </Typography>

          <Stack direction="row" justifyContent="end">
            <Stack direction="row" gap={1}>
              <TrackedButton
                trackLabel="workflowPlanning.cancelPlanning.close"
                onClick={() => setModalOpen(false)}
                variant="outlined"
              >
                Cancel action
              </TrackedButton>

              <TrackedButton
                trackLabel="workflowPlanning.cancelPlanning"
                onClick={handleClickCancelPlanning}
                variant="contained"
                color="error"
                loading={killPlanIsLoading}
                disabled={!planId || killPlanIsLoading}
              >
                Confirm cancel planning
              </TrackedButton>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default CancelPlanningModalButton;
